import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { TaskCompletionChart } from "@/components/tasks/TaskCompletionChart";
import {
  tasksApi,
  TASK_TYPE_LABELS,
  TASK_PRIORITY_LABELS,
  type TaskType,
} from "@/lib/tasks";
import { Briefcase, IdCard, Wrench, Palette } from "lucide-react";

export interface TasksSummaryWidgetProps {
  scope: "mine" | "all";
}

const PRIORITY_ORDER = [4, 3, 2, 1];

function priorityColor(priority: number): string {
  if (priority === 4) return "bg-red-500";
  if (priority === 3) return "bg-amber-500";
  if (priority === 2) return "bg-blue-500";
  return "bg-slate-400";
}

function priorityTextColor(priority: number): string {
  if (priority === 4) return "text-red-700";
  if (priority === 3) return "text-amber-700";
  if (priority === 2) return "text-blue-700";
  return "text-slate-600";
}

function getTypeIcon(type: TaskType) {
  switch (type) {
    case "Admin": return <Briefcase className="w-4 h-4 text-slate-400" />;
    case "Badge": return <IdCard className="w-4 h-4 text-slate-400" />;
    case "Production": return <Wrench className="w-4 h-4 text-slate-400" />;
    case "Artwork": return <Palette className="w-4 h-4 text-slate-400" />;
    default: return null;
  }
}

export function TasksSummaryWidget({ scope }: TasksSummaryWidgetProps) {
  const q = useQuery({
    queryKey: ["task-summary", scope],
    queryFn: () => tasksApi.summary(scope),
  });

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground animate-pulse">Loading dashboard...</p>;
  }

  if (q.isError || !q.data) {
    return (
      <p className="text-sm text-destructive">
        Could not load task summary.
      </p>
    );
  }

  const data = q.data;
  const priorityCounts = new Map(
    data.byPriority.map((p) => [p.priority, p.openCount]),
  );

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Metrics Summary Cards */}
      <div className="flex items-center gap-4">
        <div className="flex flex-col bg-white border border-slate-200 rounded-lg p-3 min-w-[140px] shadow-sm">
          <span className="text-2xl font-bold text-[#0f3453] leading-none mb-1">{data.totalOpen}</span>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Open Tasks</span>
        </div>
        <div className="flex flex-col bg-red-50/50 border border-red-100 rounded-lg p-3 min-w-[140px] shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl font-bold text-red-600 leading-none">{data.totalOverdue}</span>
            {data.totalOverdue > 0 && <span className="relative flex h-2.5 w-2.5 ml-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>}
          </div>
          <span className="text-[11px] font-bold text-red-600/80 uppercase tracking-wider">Overdue</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Types Dense List */}
        <div className="border rounded-md bg-white shadow-sm overflow-hidden">
          <div className="bg-[#0f3453] border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white">
            Tasks by Type
          </div>
          <ul className="divide-y divide-slate-100">
            {data.byType.map((t) => (
              <li key={t.taskType}>
                <Link
                  to={`/tasks?type=${t.taskType}&status=Open`}
                  className="flex items-center justify-between px-4 h-12 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all border border-transparent group-hover:border-slate-200">
                      {getTypeIcon(t.taskType)}
                    </div>
                    <span className="text-sm font-medium text-slate-900">{TASK_TYPE_LABELS[t.taskType]}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {t.overdueCount > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-red-50 text-red-700 border border-red-100">
                        {t.overdueCount} overdue
                      </span>
                    )}
                    <span className="text-sm font-semibold text-slate-700 w-6 text-right">{t.openCount}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Priority Dense List */}
        <div className="border rounded-md bg-white shadow-sm overflow-hidden">
          <div className="bg-[#0f3453] border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white">
            Priority Breakdown
          </div>
          <ul className="divide-y divide-slate-100">
            {PRIORITY_ORDER.map((priority) => {
              const count = priorityCounts.get(priority) ?? 0;
              const percentage = data.totalOpen > 0 ? (count / data.totalOpen) * 100 : 0;
              
              return (
                <li key={priority}>
                  <Link
                    to={`/tasks?status=Open`}
                    className="flex items-center justify-between px-4 h-12 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${priorityColor(priority)}`} />
                      <span className={`text-sm font-medium ${priorityTextColor(priority)}`}>
                        {TASK_PRIORITY_LABELS[priority]}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ease-in-out ${priorityColor(priority)}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-slate-700 w-6 text-right">{count}</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Compact Chart Panel */}
      <div className="border rounded-md bg-white shadow-sm overflow-hidden">
        <div className="bg-[#0f3453] border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white">
          Completion Trends (14 days)
        </div>
        <div className="p-6">
          <TaskCompletionChart series={data.completionSeries} />
        </div>
      </div>
    </div>
  );
}
